import { Config as DOMPurifyConfig } from 'dompurify';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import {
  CBCT_Anatomy,
  CBCT_Tooth_Anatomy,
} from '@/shared/api/protocol-ts/model/dto_common_anatomy_pb';
import { FeatureFlag } from '@/shared/api/protocol-ts/model/dto_organization_pb';
import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { ReportGenerationErrorCode } from '@/shared/api/protocol-ts/model/dto_report_generation_error_codes_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import {
  IOXRay_Anatomy,
  IOXRay_Tooth_Anatomy,
  Pano_Anatomy,
  Pano_Tooth_Anatomy,
} from '@/shared/api/protocol-ts/model/dto_report_type_2D_common_pb';

import { ApiServices } from '../api/api';
import type { IconNames } from '../ui/Icon/Icon.types';

import { PATHS } from './paths';
import {
  AcceptedFileTypesConfig,
  MaskGroup,
  OrganizationPermissionsKeys,
  PatientPermissionsKeys,
  ReportPermissionsKeys,
  StudyPermissionsKeys,
  ToolNames,
  UploadStudyType,
  UserPermissionsKeys,
} from './types';

export const FEATURE_FLAG_ENABLE_SCRIBE_FEATURE = 55 as FeatureFlag;
export const HAPPYDESK_IFRAME_SHOWN_CLASS = 'is-iframe-shown';

export enum SliceName {
  access = 'access',
  allowedToothConditions = 'allowedToothConditions',
  assets = 'assets',
  auth = 'auth',
  billing = 'billing',
  condition = 'condition',
  marketing = 'marketing',
  messenger = 'messenger',
  modal = 'modal',
  notification = 'notification',
  organization = 'organization',
  organizationStaff = 'organizationStaff',
  patient = 'patient',
  reports = 'reports',
  study = 'study',
  storage = 'storage',
  studyCount = 'studyCount',
  theme = 'theme',
  tooth = 'tooth',
  toothNumberEdit = 'toothNumberEdit',
  user = 'user',
  newCondition = 'newCondition',
  logicalCondition = 'logicalCondition',
  toothLandmarks = 'toothLandmarks',
  maskFilters = 'maskFilters',
  hubspot = 'hubspot',
  slices = 'slices',
  toolbarMIR = 'toolbarMIR',
  album = 'album',
  templates = 'templates',
  transcriptor = 'transcriptor',
  encounter = 'encounter',
  analyticsModel = 'analyticsModel',
  sharing = 'sharing',
  highLoad = 'highLoad',
  registryAssets = 'registryAssets',
}

export enum ToothStatus {
  unhealthy = 'unhealthy',
  lowProbability = 'lowProbability',
  treated = 'treated',
  healthy = 'healthy',
  missing = 'missing',
  notFound = 'notFound',
}
// WARN: You cannot change codes without approval from the backend team and without making changes to the documentation.
// These codes only come with the error type UserBadRequest
// https://diagnocat.atlassian.net/wiki/spaces/PW/pages/246087681/New+Design+Errors
export enum UserBadRequestError {
  INCORRECT_INVITATION_SECRET = 'incorrect_invitation_secret',
  INVITATION_RECIPIENT_ALREADY_HAS_ORGANIZATION_ROLE = 'invitation_recipient_already_has_organization_role',
  INVITATION_SECRET_INPUT_LIMIT_EXCEEDED = 'invitation_secret_input_limit_exceeded',
  MULTIPLE_PANORAMIC_XRAYS_PROVIDED = 'multiple_panoramic_xrays_provided',
  TOO_MANY_FILES_PROVIDED = 'too_many_files_provided',
  RESOURCE_IS_TOO_LARGE = 'resource_is_too_large',
  FORBIDDEN_VOLUMETRIC_DICOM = 'forbidden_volumetric_dicom',
  FORBIDDEN_BIDIMENSIONAL_DICOM = 'forbidden_bidimensional_dicom',
  FILE_IS_INVALID = 'file_is_invalid',
  LOGIN_INVALID_CREDS = 'login_invalid_creds',
  LOGIN_THROTTLED = 'login_throttled',
  RESET_THROTTLED = 'reset_throttled',
  EMAIL_TAKEN = 'email_taken',
  ALREADY_REGISTERED = 'already_registered',
  CODE_RETRIES_LIMIT = 'code_retries_limit',
  CODE_IS_EXPIRED = 'code_is_expired',
  SIGN_UP_HAS_EXPIRED = 'sign_up_has_expired',
  WRONG_CODE = 'wrong_code',
  PASSWORD_TOO_SHORT = 'password_too_short',
  PASSWORD_TOO_LONG = 'password_too_long',
  PASSWORD_TOO_COMMON = 'password_too_common',
  PASSWORD_RESET_REQUIRED = 'password_reset_required',
  // NOTE: Happens when the user is already signed in and open confirm email link from email letter
  NON_RESENDABLE_AUTH_METHOD = 'non_resendable_auth_method',
  NON_UNIQUE_PATIENT_EXTERNAL_ID = 'non_unique_patient_external_id',
  MFA_NOT_ENROLLED = 'mfa_not_enrolled',
  MFA_PENDING_MISSING = 'mfa_pending_missing',
  // Named for the cause, not the backend's ErrMFADisabled: it is returned
  // when an admin-enforced account tries to turn two-step verification off.
  MFA_ENFORCED = '2fa_disabled',
  MFA_ADMIN_TARGET = 'mfa_admin_target',
  MFA_REQUIRED = 'mfa_required',
  MFA_DISABLE_REQUIRES_LONGER_PASSWORD = 'mfa_disable_requires_longer_password',
}

export enum AlreadyExistError {
  ACCEPTED_PATIENT_SHARING = 'accepted patient sharing',
  INVITATION_TO_THIS_ORGANIZATION_ROLE = 'invitation to this organization role',
  INVITATION_TO_THIS_PATIENT = 'invitation to this patient',
}

export enum NotFoundError {
  TOKEN = 'token',
  SESSION = 'Session',
}

export const REPORT_PERMISSIONS: ReportPermissionsKeys[] = [
  'CanView',
  'CanChangeROI',
  'CanChangeConditions',
  'CanInteractWithImages',
  'CanSign',
  'CanPrint',
  'CanChangeConclusion',
  'CanChangeSettings',
  'CanDelete',
  'CanChangeReportAssets',
  'CanChangeToothComment',
  'CanChangeToothNumeration',
  'CanChangeToothApproved',
  'CanResetTooth',
  'CanChangeToothAssets',
];

export const PATIENT_PERMISSIONS: PatientPermissionsKeys[] = [
  'CanView',
  'CanViewPersonalData',
  'CanEditPersonalData',
  'CanEditPatient',
  'CanViewTeam',
  'CanEditTeam',
  'CanViewSharings',
  'CanShare',
  'CanCancelOthersSharings',
  'CanViewClinicalCaseDescription',
  'CanEditClinicalCaseDescription',
  'CanViewTasks',
  'CanEditTasks',
  'CanRequestReport',
  'CanUploadCBCT',
  'CanUploadIOXRay',
  'CanUploadPanoramicXRay',
  'CanUploadSTL',
  'CanUploadDentalPhoto',
  'CanUploadDocuments',
  'CanViewStudies',
  'CanViewReports',
];

export const USER_PERMISSIONS: UserPermissionsKeys[] = [
  'CanView',
  'CanViewPersonalData',
  'CanEditPersonalData',
  'CanEditInfo',
  'CanViewSettings',
];

export const ORGANIZATION_PERMISSIONS: OrganizationPermissionsKeys[] = [
  'CanEditSettings',
  'CanViewSettings',
  'CanView',
  'CanEditInfo',
  'CanViewStatistics',
  'CanViewAllPatients',
  'CanManageComment',
  'CanViewStaff',
  'CanEditStaff',
  'CanCreateTheirOwnPatients',
  'CanCreateOtherDoctorsPatients',
  'CanViewMyPatients',
];

export const STUDY_PERMISSIONS: StudyPermissionsKeys[] = [
  'CanView',
  'CanRequestCBCTGPReport',
  'CanRequestCBCTEndoReport',
  'CanRequestCBCTImplantReport',
  'CanRequestCBCTOrthoSlidesReport',
  'CanRequestCBCTSegmentationReport',
  'CanRequestIOXRayReport',
  'CanRequestPanoReport',
  'CanRequestDentalPhotoReport',
  'CanDelete',
];

export const ALL_PERMISSIONS = [
  ...REPORT_PERMISSIONS,
  ...PATIENT_PERMISSIONS,
  ...ORGANIZATION_PERMISSIONS,
  ...USER_PERMISSIONS,
  ...STUDY_PERMISSIONS,
];

export const USER_BAD_REQUEST_INLINE_ERROR_CODES = [
  UserBadRequestError.LOGIN_INVALID_CREDS,
  UserBadRequestError.LOGIN_THROTTLED,
  UserBadRequestError.RESET_THROTTLED,
  UserBadRequestError.PASSWORD_RESET_REQUIRED,
  UserBadRequestError.FILE_IS_INVALID,
  UserBadRequestError.INCORRECT_INVITATION_SECRET,
  UserBadRequestError.INVITATION_SECRET_INPUT_LIMIT_EXCEEDED,
  UserBadRequestError.EMAIL_TAKEN,
  UserBadRequestError.INVITATION_RECIPIENT_ALREADY_HAS_ORGANIZATION_ROLE,
  UserBadRequestError.WRONG_CODE,
  UserBadRequestError.NON_UNIQUE_PATIENT_EXTERNAL_ID,
  // Every MFA failure is reported under the code boxes by useMFAErrorMessage,
  // and mfa_required is turned into a status re-read rather than shown at all.
  UserBadRequestError.CODE_RETRIES_LIMIT,
  UserBadRequestError.CODE_IS_EXPIRED,
  UserBadRequestError.MFA_NOT_ENROLLED,
  UserBadRequestError.MFA_PENDING_MISSING,
  UserBadRequestError.MFA_ENFORCED,
  UserBadRequestError.MFA_ADMIN_TARGET,
  UserBadRequestError.MFA_REQUIRED,
  UserBadRequestError.MFA_DISABLE_REQUIRES_LONGER_PASSWORD,
];

export const REPORT_GENERATION_INLINE_ERROR_CODES = [
  ReportGenerationErrorCode.ReportGenerationErrorCode_Unspecified,
  ReportGenerationErrorCode.ReportGenerationErrorCode_GeneratedAssetUpload_Failed,
  ReportGenerationErrorCode.ReportGenerationErrorCode_ValidTimeExpired,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Modeling_CbctIosMisalignment,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Modeling_IosMissingJaw,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Modeling_RequestedToothNotFound,
  ReportGenerationErrorCode.ReportGenerationErrorCode_StudyUpload_Failed,
  ReportGenerationErrorCode.ReportGenerationErrorCode_StudyUpload_Canceled,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_IsNot2DImage,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_IsNot3DImage,
  ReportGenerationErrorCode.ReportGenerationErrorCode_FileCannotBeRead,
  ReportGenerationErrorCode.ReportGenerationErrorCode_InputData_UnexpectedStudyQuantity,
  ReportGenerationErrorCode.ReportGenerationErrorCode_InputData_UnexpectedStudyContent,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_DuplicateSlices,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_Metadata_NonDerivableVoxelSize,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_Metadata_NonDerivableRescaleInterceptAttribute,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_Metadata_NonDerivableRescaleSlopeAttribute,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_Metadata_NonDerivableImagePositionPatientAttribute,
  ReportGenerationErrorCode.ReportGenerationErrorCode_Dicom_Metadata_UnexpectedModality,
];

export const INLINE_ERROR_CODES = [
  ...USER_BAD_REQUEST_INLINE_ERROR_CODES,
  ...REPORT_GENERATION_INLINE_ERROR_CODES,
];

export enum ToothIconPathByToothStatus {
  unhealthy = 'Unhealthy',
  lowProbability = 'LowProbability',
  treated = 'Treated',
  healthy = 'Healthy',
}

export const REFRESH_TOKEN_COOLDOWN_MS = 240000;

export enum PrintType {
  blackAndWhite = 'black-and-white',
  colored = 'colored',
}

export const THIRD_MOLAR_TOOTH_NUMBER = [18, 28, 38, 48];

export const MOLAR_TOOTH_NUMBER = [
  ...THIRD_MOLAR_TOOTH_NUMBER,
  17,
  16,
  27,
  26,
  37,
  36,
  47,
  46,
];

export const DEFAULT_YEAR_OFFSET: number = 10;

export const DEFAULT_MEDICAL_IMAGE_HEIGHT = 140;
export const DEFAULT_MEDICAL_IMAGE_HEIGHT_MOBILE = 86;

export enum ToastErrors {
  errorHeading = 'errorHeading',
  wrongFormat = 'wrongFormat',
  defaultError = 'defaultError',
}

export const UPLOADING_IMAGE_MAX_FILES = 50;
export const UPLOADING_IMAGE_MAX_SIZE = 5242880; //5Mb
export const UPLOADING_DENTAL_PHOTO_MAX_SIZE = 26214400; //25Mb
export const UPLOADING_DENTAL_PHOTO_ZIP_MAX_SIZE = 1073741824; //1Gb
export const UPLOADING_DENTAL_VIDEO_MAX_SIZE = 1073741824; //1Gb
export const UPLOADING_IMAGE_ACCEPTED_FORMATS = ['.png', '.jpg', '.jpeg'];

export const DROPZONE_IMAGE_FORMATS = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg'],
  'image/jpg': ['.jpg'],
  'image/tiff': ['.tiff', '.tif', '.xtf'],
  'image/x-tiff': ['.tiff', '.tif', '.xtf'],
  'image/bmp': ['.bmp'],
};

export const DROPZONE_VIDEO_FORMATS = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
};
export const SAFE_AVATAR_IMAGE_FORMATS = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
};
export const DROPZONE_ARCHIVE_FORMATS = {
  'application/zip': ['.zip'],
};
export const DICOM_FILE_EXTENSIONS = ['.dcm'];
// X-ray software also saves DICOM as .dic. CBCT does not accept it: its
// slices go through a separate DICOM pipeline that was not checked with .dic.
export const XRAY_DICOM_FILE_EXTENSIONS = [...DICOM_FILE_EXTENSIONS, '.dic'];
export const DROPZONE_DICOM_FORMAT = {
  'application/dicom': [...DICOM_FILE_EXTENSIONS],
};
export const DROPZONE_XRAY_DICOM_FORMAT = {
  'application/dicom': [...XRAY_DICOM_FILE_EXTENSIONS],
};
const BASE_STL_FORMATS = {
  'application/sla': ['.stl'],
  'model/obj': ['.obj'],
  'application/x-ply': ['.ply'],
  'application/x-drc': ['.drc'],
};
const TEXT_PLAIN_STL_FORMATS = {
  base: ['.obj', '.ply'],
  withMtl: ['.obj', '.ply', '.mtl'],
};
export const DROPZONE_STL_FORMATS = {
  ...BASE_STL_FORMATS,
  'text/plain': TEXT_PLAIN_STL_FORMATS.base,
};
export const DROPZONE_STL_FORMATS_FOR_IOS_SEGMENTATION_REPORT = {
  ...BASE_STL_FORMATS,
  'text/plain': TEXT_PLAIN_STL_FORMATS.withMtl,
};

export const ACCEPTED_FILE_TYPES_FOR_DROPZONE_BY_ASSET_TYPE: Record<
  UploadStudyType,
  AcceptedFileTypesConfig
> = {
  [AssetType.AssetType_Study_CBCT]: {
    ...DROPZONE_DICOM_FORMAT,
    ...DROPZONE_ARCHIVE_FORMATS,
  },
  [AssetType.AssetType_Study_IntraoralXRay]: {
    ...DROPZONE_XRAY_DICOM_FORMAT,
    ...DROPZONE_ARCHIVE_FORMATS,
    ...DROPZONE_IMAGE_FORMATS,
  },
  [AssetType.AssetType_Study_PanoramicXRay]: {
    ...DROPZONE_XRAY_DICOM_FORMAT,
    ...DROPZONE_ARCHIVE_FORMATS,
    ...DROPZONE_IMAGE_FORMATS,
  },
  [AssetType.AssetType_Study_DentalPhoto]: {
    ...DROPZONE_ARCHIVE_FORMATS,
    ...DROPZONE_IMAGE_FORMATS,
    ...DROPZONE_VIDEO_FORMATS,
  },
  [AssetType.AssetType_Study_IOS_Meshes]: {
    ...DROPZONE_ARCHIVE_FORMATS,
    ...DROPZONE_STL_FORMATS,
  },
  [AssetType.AssetType_Document]: {
    'text/plain': [],
  },
};

export const ACCEPTED_FILE_TYPES_FOR_DROPZONE_FOR_IOS_SEGMENTATION_REPORT: AcceptedFileTypesConfig =
  {
    ...DROPZONE_ARCHIVE_FORMATS,
    ...DROPZONE_STL_FORMATS_FOR_IOS_SEGMENTATION_REPORT,
    ...DROPZONE_IMAGE_FORMATS,
  };

export const DEFAULT_LOCALE = 'en-US';

// Maybe make separate file for animation constants?
export const MODAL_ANIMATION_DURATION = 0.15;
export const MOBILE_MODAL_ANIMATION_DURATION = 0.3;
export const TEXT_TOOLTIP_DELAY_DURATION_MS = 600;

export const MIN_PASSWORD_LENGTH = 15;
export const MAX_PASSWORD_LENGTH = 256;

export const PERSON_NAME_REGEX = /^[\p{L}]+(?:[ \-−][\p{L}]+)*$/u;

export const PHONE_NUMBER_REGEX = /^\+?\d+$/;

// Field length validation constants
// General text fields (comments, descriptions, etc.)
export const MAX_TEXT_FIELD_LENGTH = 350;

// Specific field type limits based on business logic and standards
export const MAX_NAME_LENGTH = 100; // Person/company names
export const MAX_EMAIL_LENGTH = 254; // RFC 5321 standard
export const MAX_PHONE_LENGTH = 30; // E.164 format with formatting
export const MAX_EXTERNAL_ID_LENGTH = 100; // External identifiers (patient, user)
export const MAX_ADDRESS_LENGTH = 200; // Address lines
export const MAX_CITY_LENGTH = 100; // City names
export const MAX_REGION_LENGTH = 100; // State/region names
export const MAX_ZIP_CODE_LENGTH = 20; // Postal codes
export const MAX_TAX_ID_LENGTH = 50; // VAT/TIN numbers
export const MAX_WEBSITE_LENGTH = 200; // URLs

export const BILLING_ZONE_WITHOUT_STRIPE_REGEXP = /cis|israel/i;

export const JAW = {
  quartile1: [18, 17, 16, 15, 14, 13, 12, 11],
  quartile2: [21, 22, 23, 24, 25, 26, 27, 28],
  quartile3: [31, 32, 33, 34, 35, 36, 37, 38],
  quartile4: [48, 47, 46, 45, 44, 43, 42, 41],
  primaryQuartile1: [55, 54, 53, 52, 51],
  primaryQuartile2: [61, 62, 63, 64, 65],
  primaryQuartile3: [71, 72, 73, 74, 75],
  primaryQuartile4: [85, 84, 83, 82, 81],
};

export const LOWER_JAW_TEETH_ISO_NUMBERS = [
  ...JAW.quartile3,
  ...JAW.quartile4,
  ...JAW.primaryQuartile3,
  ...JAW.primaryQuartile4,
];

export const UPPER_JAW_TEETH_ISO_NUMBERS = [
  ...JAW.quartile1,
  ...JAW.quartile2,
  ...JAW.primaryQuartile1,
  ...JAW.primaryQuartile2,
];

export const NEW_LINE = '\n';

export const IGNORE_INTERNAL_SERVER_ERROR_SERVICES: ApiServices[] = [
  'marketing',
  'hubspot',
];

export const TOOTH_TYPE_CODES = [
  ConditionCode.CodeTooth,
  ConditionCode.Implant,
  ConditionCode.Pontic,
  ConditionCode.RootFragment,
  ConditionCode.Missing,
  ConditionCode.ToothGerm,
];

export const MASK_FILTERS_RESTORATIVE_CODES = [
  ConditionCode.Attrition,
  ConditionCode.Abfraction,
  ConditionCode.CrownFracture,
  ConditionCode.RestorationFracture,
  ConditionCode.PreEruptiveResorption,
  ConditionCode.VoidsInTheFilling,
  ConditionCode.Overhang,
  ConditionCode.OpenMargin,
  ConditionCode.LackOfInterproximalContact,
  ConditionCode.ToothStump,
  ConditionCode.CariesSigns,
  ConditionCode.SecondaryCaries,
  // FDA 2.0 only diagnoses (WA-6322): masks are unions of prod restorative masks
  ConditionCode.FDA_Caries,
  ConditionCode.FDA_RestorativeImperfections,
  ConditionCode.Child_DefectDepth_Enamel,
  ConditionCode.Child_DefectDepth_Dentin,
  ConditionCode.Child_DefectDepth_WithPulpExposure,
  ConditionCode.Child_DefectDepth_Initial,
  ConditionCode.Child_DefectDepth_NotDefined,
  ConditionCode.RootFragment,
];

export const MASK_FILTERS_ENDO_CODES = [
  ConditionCode.CervicalResorption,
  ConditionCode.Extrusion,
  ConditionCode.Intrusion,
  ConditionCode.LateralLuxation,
  ConditionCode.CervicalResorption,
  ConditionCode.EndodonticAccess,
  ConditionCode.InternalResorption,
  ConditionCode.CanalObliteration,
  ConditionCode.Pulpotomy,
  ConditionCode.Child_EndoTreated_Obturation_ShortFilling,
  ConditionCode.Child_EndoTreated_Obturation_Overfilling,
  ConditionCode.Child_EndoTreated_Obturation_MissedCanal,
  ConditionCode.Child_EndoTreated_Complications_RootPerforation,
  ConditionCode.Child_EndoTreated_Complications_SeparatedInstrumentInTheRootCanal,
  ConditionCode.Child_EndoTreated_Quality_VoidsPresentInTheRootFilling,
  ConditionCode.PeriapicalRadiolucency,
  ConditionCode.LateralRadiolucency,
  ConditionCode.PDLSpaceWidening_AlongTheRoot,
  ConditionCode.PDLSpaceWidening_InPeriapicalRegion,
  ConditionCode.SingsOfCommunicationWithMaxillarySinus,
  ConditionCode.SignsOfInferiorAlveolarCanalInvolvement,
];

export const MASK_FILTERS_PREVIOUS_TREATMENT = [
  ConditionCode.Filling,
  ConditionCode.ArtificialCrown,
  ConditionCode.IndirectRestoration,
  ConditionCode.OrthodonticAppliance,
  ConditionCode.HealingAbutment,
  ConditionCode.EndodonticAccess,
  ConditionCode.EndoTreated,
  ConditionCode.Child_EndoTreated_Obturation_AdequateObturation,
  ConditionCode.Child_EndoTreated_Quality_AdequateDensity,
  ConditionCode.Child_EndoTreated_PostAndCore_CastPostAndCore,
  ConditionCode.Child_EndoTreated_PostAndCore_FiberglassPost,
  ConditionCode.Child_EndoTreated_PostAndCore_MetalPost,
  ConditionCode.Child_EndoTreated_PostAndCore_Post,
  ConditionCode.FDA_Child_EndoTreated_PostAndCore,
  ConditionCode.FDA_Child_EndoTreated_CanalFilling,
];

export const MASK_FILTERS_PERIO_CODES = [
  ConditionCode.PeriodontalBoneLoss,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Mild,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Moderate,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Severe,
  ConditionCode.Child_PeriodontalBoneLoss_Type_Horizontal,
  ConditionCode.Child_PeriodontalBoneLoss_Type_Vertical,
  ConditionCode.Child_PeriodontalBoneLoss_Type_Mixed,
  ConditionCode.FurcationLesion,
  ConditionCode.DentalCalculus,
  ConditionCode.Impaction,
  ConditionCode.PeriImplantitis,
];

export const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Root,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_RootCanal,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Crown,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_IncisalEdge,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_PulpChamber,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Enamel,
];

export const MASK_FILTERS_PANO_TOOTH_ANATOMY_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Root,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_RootCanal,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Crown,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_PulpChamber,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Enamel,
];

export const MASK_FILTERS_CBCT_TOOTH_ANATOMY_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Pulp,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Dentin,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Enamel,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Root,
];

export const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_DENTIN_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Crown,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Root,
];

export const MASK_FILTERS_PANO_TOOTH_ANATOMY_DENTIN_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Crown,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Root,
];

export const MASK_FILTERS_CBCT_TOOTH_ANATOMY_DENTIN_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Dentin,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Root,
];

const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_PULP_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_PulpChamber,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_RootCanal,
];
const MASK_FILTERS_PANO_TOOTH_ANATOMY_PULP_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_RootCanal,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_PulpChamber,
];

const MASK_FILTERS_CBCT_TOOTH_ANATOMY_PULP_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Pulp,
];

const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_ENAMEL_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_IncisalEdge,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Enamel,
];

const MASK_FILTERS_PANO_TOOTH_ANATOMY_ENAMEL_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Enamel,
];

const MASK_FILTERS_CBCT_TOOTH_ANATOMY_ENAMEL_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Enamel,
];

export const MASK_FILTERS_IOXRAY_MAX_FAX_ANATOMY_CODES = [
  IOXRay_Anatomy.IOXRay_Anatomy_AlveolarBone,
  IOXRay_Anatomy.IOXRay_Anatomy_MandibularCanal,
  IOXRay_Anatomy.IOXRay_Anatomy_VestibularBone,
  IOXRay_Anatomy.IOXRay_Anatomy_OralBone,
] as number[];

export const MASK_FILTERS_PANO_MAX_FAX_ANATOMY_CODES = [
  Pano_Anatomy.Pano_Anatomy_MandibularCanal,
  Pano_Anatomy.Pano_Anatomy_Maxilla,
  Pano_Anatomy.Pano_Anatomy_Mandible,
];

export const MASK_FILTERS_CBCT_MAX_FAX_ANATOMY_CODES = [
  CBCT_Anatomy.CBCT_Anatomy_MandibularCanal,
  CBCT_Anatomy.CBCT_Anatomy_Maxilla,
  CBCT_Anatomy.CBCT_Anatomy_Mandible,
];

// TODO: need to approve new anatomy codes add new filter

// TODO: temporary config for conditions with masks should be removed when anatomy will be ready
// TODO: need to move at shared mb for correct FSD
export const MASK_CONDITIONS_TO_SHOW = MASK_FILTERS_PERIO_CODES.concat(
  MASK_FILTERS_RESTORATIVE_CODES,
  MASK_FILTERS_ENDO_CODES,
  MASK_FILTERS_PREVIOUS_TREATMENT,
);

export const CONDITIONS_BY_MASK_GROUP: Record<MaskGroup, ConditionCode[]> = {
  perio: MASK_FILTERS_PERIO_CODES,
  restorative: MASK_FILTERS_RESTORATIVE_CODES,
  endo: MASK_FILTERS_ENDO_CODES,
  chart: MASK_FILTERS_PREVIOUS_TREATMENT,
};

export const PBL_RED_COLOR = '#FF3333';
export const PBL_GRAY_COLOR = '#FFFFFF';

export const PBL_RED_CODES = [
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Mild,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Moderate,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Severe,
];

export const LOW_PROBABILITY_CONDITION_CODES = [
  ConditionCode.CariesSigns,
  ConditionCode.PDLSpaceWidening_AlongTheRoot,
  ConditionCode.PDLSpaceWidening_InPeriapicalRegion,
];

export const DICOM_MIME_TYPE = 'application/dicom';

const constants = {
  SliceName,
  ToothStatus,
  UserBadRequestError,
  AlreadyExistError,
  NotFoundError,
  REPORT_PERMISSIONS,
  PATIENT_PERMISSIONS,
  ALL_PERMISSIONS,
  USER_BAD_REQUEST_INLINE_ERROR_CODES,
  REPORT_GENERATION_INLINE_ERROR_CODES,
  INLINE_ERROR_CODES,
  ToothIconPathByToothStatus,
  REFRESH_TOKEN_COOLDOWN_MS,
  PrintType,
  THIRD_MOLAR_TOOTH_NUMBER,
  MOLAR_TOOTH_NUMBER,
  DEFAULT_YEAR_OFFSET,
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  ToastErrors,
  UPLOADING_IMAGE_MAX_SIZE,
  UPLOADING_IMAGE_ACCEPTED_FORMATS,
  DEFAULT_LOCALE,
  MODAL_ANIMATION_DURATION,
  MIN_PASSWORD_LENGTH,
  MAX_PASSWORD_LENGTH,
  BILLING_ZONE_WITHOUT_STRIPE_REGEXP,
  JAW,
  LOWER_JAW_TEETH_ISO_NUMBERS,
  UPPER_JAW_TEETH_ISO_NUMBERS,
  NEW_LINE,
  IGNORE_INTERNAL_SERVER_ERROR_SERVICES,
  MASK_FILTERS_RESTORATIVE_CODES,
  MASK_FILTERS_ENDO_CODES,
  MASK_FILTERS_PREVIOUS_TREATMENT,
  MASK_FILTERS_PERIO_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_DENTIN_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_DENTIN_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_DENTIN_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_PULP_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_PULP_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_PULP_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_ENAMEL_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_ENAMEL_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_ENAMEL_CODES,
  MASK_FILTERS_IOXRAY_MAX_FAX_ANATOMY_CODES,
  MASK_FILTERS_PANO_MAX_FAX_ANATOMY_CODES,
  MASK_FILTERS_CBCT_MAX_FAX_ANATOMY_CODES,
  MASK_CONDITIONS_TO_SHOW,
  CONDITIONS_BY_MASK_GROUP,
  LOW_PROBABILITY_CONDITION_CODES,
  DICOM_MIME_TYPE,
  PBL_RED_COLOR,
  PBL_GRAY_COLOR,
  PBL_RED_CODES,
} as const;

export default constants;

export const ALLOWED_URLS = [
  'dev.diagnocat.dev',
  'staging.diagnocat.dev',
  'app.diagnocat.us',
  'us-app.diagnocato.com',
  'app.diagnocat.eu',
  'eu-app.diagnocato.com',
  'app.diagnocat.ca',
  'ca-app.diagnocato.com',
  'app.oceania.diagnocat.com',
  'oceania-app.diagnocato.com',
  'app2.diagnocat.ru',
];

export const SENTRY_CUSTOM_TAG_KEY = 'customTag';

export const SENTRY_CUSTOM_TAG = {
  refreshWidget: 'refresh-widget',
};

export const SUPERNUMERARY_INDEX_BY_USER = 999;

export const DEFAULT_AVATAR_SIZE = 48;

export const PRE_SIGN_PARAMS = {
  signature: 'X-Dc-Signature',
  clientKeyID: 'X-Dc-Client-Key-Id',
  timestampUnixMilli: 'X-Dc-Timestamp-Unix-Milli',
  userID: 'X-Dc-User-Id',
  organizationID: 'X-Dc-Organization-Id',
  reportID: 'X-Dc-Report-Id',
} as const;

export const REQUIRED_PRESIGN_PARAMS = [
  PRE_SIGN_PARAMS.signature,
  PRE_SIGN_PARAMS.clientKeyID,
  PRE_SIGN_PARAMS.timestampUnixMilli,
  PRE_SIGN_PARAMS.userID,
] as const;

export const REPORT_GENERATION_AVERAGE_TIME: Record<ReportType, number> = {
  [ReportType.ReportType_InvalidValue]: 33_000,
  [ReportType.ReportType_Pano_GP]: 33_000,
  [ReportType.ReportType_IOXRay_GP]: 14_000,
  [ReportType.ReportType_CBCT_GP]: 309_000,
  [ReportType.ReportType_CBCT_OrthoSlides]: 559_000,
  [ReportType.ReportType_CBCT_Segmentation]: 327_000,
  [ReportType.ReportType_CBCT_IOS_Superimposition]: 477_000,
  [ReportType.ReportType_StudioImplant]: 934_000,
  [ReportType.ReportType_StudioOrtho]: 568_000,
  [ReportType.ReportType_CBCT_Implant]: 309_000,
  [ReportType.ReportType_CBCT_Endo]: 309_000,
  [ReportType.ReportType_CBCT_Molar]: 309_000,
  [ReportType.ReportType_DentalPhoto_GP]: 33_000,
  [ReportType.ReportType_IOS_Segmentation]: 33_000,
  [ReportType.ReportType_Pano_Bitewings]: 33_000,
};

// WebKit (Safari macOS/iOS) does not render the first frame of a <video> with
// preload="metadata" unless an explicit poster is set. Appending a media
// fragment makes Safari seek to and display that frame; the offset is small
// enough that playback from controls is visually indistinguishable from 0:00.
export const VIDEO_POSTER_FRAGMENT = '#t=0.001';

// Session-storage key the patient page restores its scroll offset from; cards
// that navigate away from the page save the offset under the same key.
export const PATIENT_PROFILE_SCROLL_KEY_PREFIX = 'scroll_patient_';
export const DENTAL_PHOTO_WIDGET_SCROLL_KEY =
  'dental-photo-widget-scroll-target';

export const TEMPLATES_TAB = 'Templates';

export const TEMPLATES_TAB_PATH = `${PATHS.companySettings}?tab=${TEMPLATES_TAB}`;

export const DEMO_PATH_PREFIX = '/demo';

// Formatting produced by the conclusion editor (draft-js-export-html output).
// Everything else — <script>, event handlers, javascript: URLs — is stripped.
export const SAFE_HTML_SANITIZER_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    'p',
    'br',
    'div',
    'span',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'ins',
    's',
    'del',
    'strike',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ol',
    'ul',
    'li',
    'a',
    'img',
    'sub',
    'sup',
    'blockquote',
    'pre',
    'code',
  ],
  ALLOWED_ATTR: ['style', 'href', 'target', 'rel', 'src', 'alt', 'start'],
};

// Echo cancellation is off: recording is not a call — there is no far-end
// playback to cancel and it only distorts speech.
export const MICROPHONE_AUDIO_PROFILE: MediaTrackConstraints = {
  channelCount: { ideal: 1 },
  sampleRate: { ideal: 48000 },
  echoCancellation: { ideal: false },
  noiseSuppression: { ideal: true },
  autoGainControl: { ideal: true },
};

export const LANGUAGE_FILTER_ALL = 'all' as const;

export const TOOL_NAME_TO_ICON_MAPPING: Record<ToolNames, IconNames> = {
  ruler: 'ruler',
  arrow: 'arrow',
  angle: 'angle',
  polyline: 'polyline',
  eraser: 'erase',
  view: 'eyeFilled',
  brightness: 'brightness',
  sharpening: 'sharpness',
  expand: 'expand',
  invert: 'invert',
  reset: 'back',
  move: 'move',
  zoom: 'zoom',
  split: 'split',
  download: 'download',
  sharpness: 'sharpness',
  editNumbers: 'toothnum',
  ios: 'ios',
  turn: 'turn',
  hideMarks: 'close',
  editMask: 'pen',
} as const;
